import type { ImportIssue, ImportPreview, RawQuestionRecord, SupportedQuestionType } from './importTypes';

const levels = new Set(['playgroup', 'nursery', 'kg', 'class 1', 'class 2', 'class 3', 'class 4', 'class 5', 'pg', 'nur', 'c1', 'c2', 'c3', 'c4', 'c5']);
const subjects = new Set(['english', 'urdu', 'mathematics', 'science', 'social studies', 'islamiat', 'eng', 'urd', 'math', 'sci', 'sst', 'isl']);
const questionTypes = new Set<SupportedQuestionType>([
  'single_choice', 'picture_choice', 'picture_count', 'true_false', 'matching',
  'sequence', 'fill_blank', 'multi_select', 'short_answer',
]);

const issueMessages: Record<string, string> = {
  missing_id: 'Question ID is required.',
  missing_grade: 'Grade or level is required.',
  missing_subject: 'Subject is required.',
  missing_type: 'Question type is required.',
  missing_prompt: 'Question prompt is required.',
  missing_answer: 'A private answer key is required.',
  unknown_grade: 'Grade does not match a configured QuizPiko level.',
  unknown_subject: 'Subject does not match a configured QuizPiko subject.',
  unsupported_question_type: 'Question type is not supported by the current quiz engine.',
  invalid_difficulty: 'Difficulty must be a number from 1 to 5.',
  insufficient_options: 'This question type requires at least two options.',
  insufficient_matching_items: 'Matching questions require at least two left and two right items.',
  insufficient_sequence_items: 'Sequence questions require at least two items.',
  duplicate_id_in_file: 'The same question ID appears more than once in this file.',
  duplicate_content_in_file: 'An identical question signature appears more than once in this file.',
  missing_explanation: 'Explanation is missing and should be added before publication.',
  missing_curriculum_alignment: 'Curriculum alignment is missing.',
  visual_question_without_asset_id: 'Picture-based question has no 3D asset ID.',
  urdu_language_without_urdu_script: 'Urdu-language record does not appear to contain Urdu script.',
};

function normalize(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => `${JSON.stringify(key)}:${stable(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function contentKey(record: RawQuestionRecord): string {
  return [
    normalize(record.grade), normalize(record.subject), normalize(record.type),
    normalize(record.language || 'en'), normalize(record.prompt),
    stable(record.options || []), stable(record.left_items || []), stable(record.right_items || []),
  ].join('|');
}

function count(target: Record<string, number>, key: unknown) {
  const normalizedKey = String(key || 'Unknown');
  target[normalizedKey] = (target[normalizedKey] || 0) + 1;
}

function addIssue(issues: ImportIssue[], row: number, record: RawQuestionRecord, severity: 'error' | 'warning', code: string) {
  issues.push({
    row,
    externalId: String(record.id || '—'),
    severity,
    code,
    message: issueMessages[code] || code,
  });
}

function hasAssetId(record: RawQuestionRecord): boolean {
  return stable(record).includes('"asset_id"');
}

function validateRecord(record: RawQuestionRecord, row: number, issues: ImportIssue[]) {
  const id = normalize(record.id);
  const grade = normalize(record.grade);
  const subject = normalize(record.subject);
  const type = normalize(record.type) as SupportedQuestionType;
  const prompt = normalize(record.prompt);
  const difficulty = Number(record.difficulty ?? 1);

  if (!id) addIssue(issues, row, record, 'error', 'missing_id');
  if (!grade) addIssue(issues, row, record, 'error', 'missing_grade');
  if (!subject) addIssue(issues, row, record, 'error', 'missing_subject');
  if (!type) addIssue(issues, row, record, 'error', 'missing_type');
  if (!prompt) addIssue(issues, row, record, 'error', 'missing_prompt');
  if (!Object.prototype.hasOwnProperty.call(record, 'answer') || record.answer === null) addIssue(issues, row, record, 'error', 'missing_answer');
  if (grade && !levels.has(grade)) addIssue(issues, row, record, 'error', 'unknown_grade');
  if (subject && !subjects.has(subject)) addIssue(issues, row, record, 'error', 'unknown_subject');
  if (type && !questionTypes.has(type)) addIssue(issues, row, record, 'error', 'unsupported_question_type');
  if (!Number.isInteger(difficulty) || difficulty < 1 || difficulty > 5) addIssue(issues, row, record, 'error', 'invalid_difficulty');

  if (['single_choice', 'picture_choice', 'picture_count', 'multi_select'].includes(type) && (!Array.isArray(record.options) || record.options.length < 2)) {
    addIssue(issues, row, record, 'error', 'insufficient_options');
  }
  if (type === 'matching' && (!Array.isArray(record.left_items) || record.left_items.length < 2 || !Array.isArray(record.right_items) || record.right_items.length < 2)) {
    addIssue(issues, row, record, 'error', 'insufficient_matching_items');
  }
  if (type === 'sequence') {
    const items = Array.isArray(record.items) ? record.items : record.options;
    if (!Array.isArray(items) || items.length < 2) addIssue(issues, row, record, 'error', 'insufficient_sequence_items');
  }

  if (!normalize(record.explanation)) addIssue(issues, row, record, 'warning', 'missing_explanation');
  const curriculum = record.curriculum as Record<string, unknown> | undefined;
  if (!normalize(curriculum?.alignment)) addIssue(issues, row, record, 'warning', 'missing_curriculum_alignment');
  if (['picture_choice', 'picture_count', 'matching'].includes(type) && !hasAssetId(record)) addIssue(issues, row, record, 'warning', 'visual_question_without_asset_id');
  if (normalize(record.language) === 'ur' && prompt && !/[ء-ی]/.test(String(record.prompt))) addIssue(issues, row, record, 'warning', 'urdu_language_without_urdu_script');
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') { current += '"'; i += 1; }
      else quoted = !quoted;
    } else if (char === ',' && !quoted) {
      result.push(current); current = '';
    } else current += char;
  }
  result.push(current);
  return result;
}

function parseCsv(text: string): RawQuestionRecord[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]).map((header) => header.trim());
  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const record: RawQuestionRecord = {};
    headers.forEach((header, index) => {
      const value = cells[index] ?? '';
      if (['options', 'answer', 'tags', 'curriculum', 'stimulus', 'left_items', 'right_items', 'items'].includes(header)) {
        try { record[header] = JSON.parse(value); } catch { record[header] = value; }
      } else if (header === 'difficulty') record[header] = Number(value);
      else record[header] = value;
    });
    return record;
  });
}

export function parseQuestionText(text: string, filename: string): RawQuestionRecord[] {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (extension === 'jsonl') {
    return text.split(/\r?\n/).filter((line) => line.trim()).map((line, index) => {
      try { return JSON.parse(line) as RawQuestionRecord; }
      catch { throw new Error(`Invalid JSON on JSONL row ${index + 1}.`); }
    });
  }
  if (extension === 'json') {
    const value = JSON.parse(text) as unknown;
    if (Array.isArray(value)) return value as RawQuestionRecord[];
    if (value && typeof value === 'object' && Array.isArray((value as { questions?: unknown[] }).questions)) {
      return (value as { questions: RawQuestionRecord[] }).questions;
    }
    throw new Error('JSON must contain an array or an object with a questions array.');
  }
  if (extension === 'csv') return parseCsv(text);
  throw new Error('Choose a JSONL, JSON or CSV file.');
}

export function validateQuestionRecords(records: RawQuestionRecord[]): ImportPreview {
  const issues: ImportIssue[] = [];
  const ids = new Map<string, number[]>();
  const signatures = new Map<string, number[]>();
  const countsByLevel: Record<string, number> = {};
  const countsBySubject: Record<string, number> = {};
  const countsByType: Record<string, number> = {};

  records.forEach((record, index) => {
    const row = index + 1;
    validateRecord(record, row, issues);
    count(countsByLevel, record.grade);
    count(countsBySubject, record.subject);
    count(countsByType, record.type);
    const id = normalize(record.id);
    const signature = contentKey(record);
    if (id) ids.set(id, [...(ids.get(id) || []), row]);
    if (signature) signatures.set(signature, [...(signatures.get(signature) || []), row]);
  });

  ids.forEach((rows) => {
    if (rows.length > 1) rows.forEach((row) => addIssue(issues, row, records[row - 1], 'error', 'duplicate_id_in_file'));
  });
  signatures.forEach((rows) => {
    if (rows.length > 1) rows.forEach((row) => addIssue(issues, row, records[row - 1], 'error', 'duplicate_content_in_file'));
  });

  const rowsWithErrors = new Set(issues.filter((issue) => issue.severity === 'error').map((issue) => issue.row));
  const rowsWithWarnings = new Set(issues.filter((issue) => issue.severity === 'warning').map((issue) => issue.row));
  return {
    records,
    issues: issues.sort((a, b) => a.row - b.row || a.severity.localeCompare(b.severity)),
    totalRows: records.length,
    validRows: records.length - rowsWithErrors.size,
    warningRows: [...rowsWithWarnings].filter((row) => !rowsWithErrors.has(row)).length,
    invalidRows: rowsWithErrors.size,
    countsByLevel,
    countsBySubject,
    countsByType,
  };
}
